import { Table, TDynamicTables } from "@/components/kit/Table/Table";
import { useQueryParams } from "@/hooks/general/useQueryParams";
import SvgIcon from "@/components/common/SvgIcon";
import classNames from "classnames";
import { ESort, TUserParamsType } from "@/features/organizations/types";
import MspAdminRow from "./MspAdminRow";
import { IUserResponse } from "../types";
import { useState } from "react";
import RevokeUserDialog from "./RevokeUserDialog";
import useRemoveUser from "@/hooks/query/admins/useRemoveUser";
import { useQueryClient } from "@tanstack/react-query";

const defaultColumnConfig: TDynamicTables["fields"] = [
  {
    widths: "18%",
    title: "Name",
    visibility: true,
    index: 0,
    orderBy: "name",
  },
  {
    widths: "18%",
    title: "Display name",
    visibility: true,
    index: 1,
  },
  {
    widths: "18%",
    title: "Email",
    visibility: true,
    index: 2,
  },
  {
    widths: "18%",
    title: "MFA",
    visibility: true,
    index: 3,
  },
  {
    widths: "18%",
    title: "Last login",
    visibility: true,
    index: 4,
  },

  {
    widths: "10%",
    title: "Actions",
    visibility: true,
    index: 5,
  },
];

interface DeviceGroupsTableProps {
  data: IUserResponse[];
  onViewDetails: (policy: IUserResponse) => void;
  loading: boolean;
}

const MspAdminTable = ({
  data,
  onViewDetails,
  loading,
}: DeviceGroupsTableProps) => {
  const { setParams, params } = useQueryParams<TUserParamsType>();
  const columnWidths = defaultColumnConfig.map((elm) => elm.widths);
  const [selectedUser, setSelectedUser] = useState<IUserResponse | null>(null);
  const { mutate: revokeUser, isPending: isRevokingUser } = useRemoveUser();
  const queryClient = useQueryClient();
  const handleRevokeUser = (id: string, callback?: () => void) => {
    revokeUser(id, {
      onSuccess: () => {
        setSelectedUser(null);
        queryClient.invalidateQueries({ queryKey: ["users"] });
        callback?.();
      },
    });
  };
  return (
    <>
      <Table.Root
        defaultColumnConfig={defaultColumnConfig}
        columnConfig={defaultColumnConfig}
        columnWidths={columnWidths as string[]}
        columnControl={false}
      >
        <Table.Header>
          <Table.RowHeader>
            {defaultColumnConfig.map((elm, index) => (
              <Table.ColumnHeader
                name={elm.title}
                key={index}
                hasAction={false}
                moveable={false}
                hasColumnControl={false}
                index={elm.index}
              >
                <span
                  title={elm.title}
                  className="truncate !font-bold items-center gap-2"
                >
                  {elm.title !== "" ? elm.title : ""}
                </span>
                {elm.orderBy && params.orderBy === elm.orderBy && (
                  <SvgIcon
                    iconName="linearArrow"
                    fill={
                      params.orderBy === elm.orderBy
                        ? "fill-gray-500"
                        : "fill-gray-300"
                    }
                    className={classNames({
                      "rotate-180":
                        params.orderBy === elm.orderBy &&
                        params.orderDirection === "DESC",
                    })}
                  />
                )}
                <Table.ColumnHeaderAction
                  removeable={false}
                  lists={
                    elm.orderBy
                      ? [
                          {
                            id: "ascending",
                            title: "Sort ascending",
                            icon: "sortFromBottomToTop",
                            visible: !(
                              params.orderDirection === "ASC" &&
                              params.orderBy === elm.orderBy
                            ),
                            onClick: () =>
                              setParams({
                                ...params,
                                orderBy: elm.orderBy as any,
                                orderDirection: ESort.ASC,
                              }),
                          },
                          {
                            id: "descending",
                            title: "Sort descending",
                            icon: "sortFromTopToBottom",
                            visible: !(
                              params.orderDirection === "DESC" &&
                              params.orderBy === elm.orderBy
                            ),
                            onClick: () =>
                              setParams({
                                ...params,
                                orderBy: elm.orderBy as any,
                                orderDirection: ESort.DESC,
                              }),
                          },
                        ]
                      : []
                  }
                  index={elm.index}
                />
              </Table.ColumnHeader>
            ))}
          </Table.RowHeader>
        </Table.Header>
        <Table.Body isLoading={loading} skeletonLength={6}>
          {data?.map((user) => (
            <MspAdminRow
              onViewDetails={onViewDetails}
              key={user?.id}
              user={user}
              setSelectedUser={setSelectedUser}
            />
          ))}
          {/* {data?.map((policy) => (
          <MspAdminRow
            onViewDetails={onViewDetails}
            key={policy?.id}
            organization={policy}
          />
        ))} */}
        </Table.Body>
      </Table.Root>
      <RevokeUserDialog
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        onRevokeUser={handleRevokeUser}
        isRevokingUser={isRevokingUser}
      />
    </>
  );
};

export default MspAdminTable;
